import React, { useState, useCallback, useEffect } from 'react';
import differenceWith from 'lodash/differenceWith';
import isEqual from 'lodash/isEqual';
import findIndex from 'lodash/findIndex';
import classnames from 'classnames';

import { downloadFileInZoom } from 'Shared/utils/Attachment.utils';
import { downloadFile } from 'Shared/helpers';

// Types
import { Attachment } from 'Shared/types/Attachment.type';

// Components
import FileUploader from './FileUploader/FileUploader';

import AttachmentItem from './AttachmentItem';

// Hooks
import { useApp } from 'Shared/utils/hooks/use-app';

import './AttachmentsContainer.scss';

const DEFAULT_OPTIONS = {
  showPreview: true,
  view: 'grid' as OPTIONS_VIEW_TYPE,
};

export type OPTIONS_VIEW_TYPE = 'grid' | 'list';

type Props = {
  attachments: Array<Attachment>;
  fileUploaderRef?: { current: HTMLInputElement };
  allowMultipleUploads?: boolean;
  enableFileUploader?: boolean;
  options?: {
    showPreview?: boolean;
    view?: OPTIONS_VIEW_TYPE;
  };
  handleTogglePreview?: Function;
  handleUploadAttachments?: Function;
  handleDeleteAttachment?: Function;
  handleUpdateShowPreview?: Function;
};

const AttachmentsContainer = ({
  attachments,
  fileUploaderRef,
  handleUploadAttachments,
  enableFileUploader = false,
  allowMultipleUploads = true,
  handleDeleteAttachment,
  handleUpdateShowPreview,
  options = DEFAULT_OPTIONS,
}: Props) => {
  const [filesInProgress, setFilesInProgress] = useState([]);
  const [uploadSuccessedFiles, setUploadSuccessedFiles] = useState([]);

  const combinedOptions = { ...DEFAULT_OPTIONS, ...options };

  const fileUpload = file => {
    handleUploadAttachments(file).then(() => {
      setUploadSuccessedFiles(files => files.concat(file));
    });
  };

  const handleFileUpload = useCallback(
    event => {
      const { files } = event.target;
      setFilesInProgress(Array.from(files));
      const filteredFiles = [...files].filter(file => file.type.length);
      filteredFiles.forEach(file => {
        fileUpload(file);
      });
    },
    [fileUpload, setFilesInProgress]
  );

  useEffect(() => {
    const inProgressCount = filesInProgress.length;
    const uploadedCount = uploadSuccessedFiles.length;
    if (inProgressCount > 0 && uploadedCount > 0) {
      const newFilesInProgress = differenceWith(filesInProgress, uploadSuccessedFiles, isEqual);
      setFilesInProgress(newFilesInProgress);
    }
  }, [filesInProgress, setFilesInProgress, uploadSuccessedFiles]);

  const isFileInProgress = attachment => {
    const fileName = attachment.fileName;
    return findIndex(filesInProgress, f => f.name === fileName) !== -1;
  };

  const { isZoom } = useApp();

  const handleDownload = attachment => {
    if (isZoom) {
      downloadFileInZoom(`${window.location.origin}${attachment.url}`);
    } else {
      downloadFile({ url: attachment.url, filename: attachment.fileName });
    }
  };

  const isUploading = filesInProgress?.length > 0;
  const isPreviewEnabled = options.showPreview;
  const isList = combinedOptions.view === 'list';

  const baseClassName = 'AttachmentsContainer';
  const classes = classnames(baseClassName, `${baseClassName}--${combinedOptions.view}`);

  const fileUploadingClasses = classnames(
    `${baseClassName}__attachment`,
    `${baseClassName}__uploading`,
    !isPreviewEnabled || isList ? `${baseClassName}__uploading--slim` : false
  );

  function sortComparison(a, b) {
    const attachmentADate = a.createdAt.toLowerCase();
    const attachmentBDate = b.createdAt.toLowerCase();

    let comparison = 0;
    if (attachmentADate > attachmentBDate) {
      comparison = 1;
    } else if (attachmentADate < attachmentBDate) {
      comparison = -1;
    }
    return comparison;
  }

  // if (!attachments) return null;

  return (
    <>
      <div className={classes}>
        {isUploading &&
          filesInProgress.map(file => {
            return (
              <div className={fileUploadingClasses} key={file.name}>
                <div className={`${baseClassName}__uploading__footer`}>
                  <svg
                    width={20}
                    height={20}
                    viewBox="0 0 50 50"
                    className={`${baseClassName}__uploading__footer__spinner`}
                  >
                    <path d="M43.935 25.145c0-10.318-8.364-18.683-18.683-18.683-10.318 0-18.683 8.365-18.683 18.683h4.068c0-8.071 6.543-14.615 14.615-14.615s14.615 6.543 14.615 14.615h4.068z">
                      <animateTransform
                        attributeType="xml"
                        attributeName="transform"
                        type="rotate"
                        from="0 25 25"
                        to="360 25 25"
                        dur="0.6s"
                        repeatCount="indefinite"
                      />
                    </path>
                  </svg>
                  <p className={`${baseClassName}__uploading__footer__label`}>{file.name}</p>
                </div>
              </div>
            );
          })}
        {attachments
          .sort(sortComparison)
          .reverse()
          .map(attachment => {
            if (isFileInProgress(attachment)) return null;
            const uuid = attachment.uuid;
            return (
              <div key={uuid} className={`${baseClassName}__attachment`}>
                <AttachmentItem
                  attachment={attachment}
                  previewOpen={options.showPreview}
                  handleDeleteAttachment={handleDeleteAttachment}
                  handleUpdateShowPreview={handleUpdateShowPreview}
                  handleDownload={handleDownload}
                />
              </div>
            );
          })}
      </div>
      {enableFileUploader && (
        <FileUploader
          fileUploaderRef={fileUploaderRef}
          handleFileInputChange={handleFileUpload}
          allowMultipleUploads={allowMultipleUploads}
        />
      )}
    </>
  );
};

export default AttachmentsContainer;
